import {
  Checkbox,
  Input,
  InputIconWrapper,
  NumberInput,
  TextArea
} from '@library/components/inputs';
import {
  FormActionButtons,
  ResetIconButton
} from '@library/components/buttons';
import type {
  RoleWithPermissions,
  UpdateRolesRequest
} from '@library/types/role';
import { useEffect, useMemo, useState } from 'react';
import type { CheckboxOnChangeArg } from '@library/types/utils';
import type { FormattedPermission } from '@library/types/permissions';
import { NUMBER_INPUT_DEFAULT_CLASSES } from '@library/components/inputs/NumberInput';
import { sortByKey } from '@bo-utils/common';
import { usePermissions } from '@bo-hooks/permissions/usePermissions';

export interface EditRolePermissionsProps {
  enabledPermissionIds: Array<string>;
  onClose: () => void;
  onSave: (role: UpdateRolesRequest) => void;
  role: RoleWithPermissions;
}

export const EditRolePermissions = ({
  enabledPermissionIds,
  onClose,
  onSave,
  role
}: EditRolePermissionsProps) => {
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const [roleName, setRoleName] = useState(role?.name ?? '');
  const [roleDescription, setRoleDescription] = useState(
    role?.description ?? ''
  );
  const [permissions, setPermissions] = useState<
    Array<FormattedPermission> | undefined
  >(undefined);
  const [ogPermissions, setOgPermissions] = useState<
    Array<FormattedPermission> | undefined
  >(undefined);

  const { isPermissionsLoading, permissionsData } = usePermissions();

  useEffect(() => {
    if (!isPermissionsLoading && permissionsData) {
      const formattedPermissions: Array<FormattedPermission> =
        permissionsData.map((permission) => ({
          ...permission,
          hasPermission: enabledPermissionIds.includes(permission.id)
        }));

      const sortedFormattedPermissions = sortByKey(
        formattedPermissions,
        'name'
      );

      setPermissions(sortedFormattedPermissions);
      setOgPermissions(sortedFormattedPermissions);
    }
  }, [enabledPermissionIds, isPermissionsLoading, permissionsData]);

  const permissionsValueChange = (
    id: string,
    key: string,
    value: string | boolean
  ) => {
    setPermissions((prevPermissions) =>
      prevPermissions?.map((permission) =>
        permission.id === id ? { ...permission, [key]: value } : permission
      )
    );
  };

  const handlePermissionChange = ({ id, checked }: CheckboxOnChangeArg) => {
    permissionsValueChange(id, 'hasPermission', checked);
  };

  // TODO: update this function to handle transaction limit fields
  // possibly use TransactionMaxValues type for easier handling
  const handleTransactionMaxChange = (id: string, value: string) => {
    permissionsValueChange(id, 'transaction_limits', value);
  };

  const handleResetPermissions = () => {
    setPermissions(ogPermissions);
  };

  const handleSave = () => {
    setIsSaveDisabled(true);

    onSave({
      ...role,
      name: roleName,
      description: roleDescription
      // TODO: Add permissionIds and transactionMaxes once roles endpoint is updated
      // permissionIds:
      //   permissions
      //     ?.filter((permission) => permission.hasPermission)
      //     .map((permission) => permission.id) ?? []
      // transactionMaxes
    });
  };

  const saveDisabled = useMemo(
    () => isSaveDisabled || !roleName,
    [isSaveDisabled, roleName]
  );

  const permissionsContent = permissions?.map((permission) => (
    <div key={permission.name}>
      <Checkbox
        id={permission.id}
        wrapperClassName="hover:bg-soFresh-120 flex flex-row-reverse justify-between"
        className="size-5"
        checked={permission.hasPermission}
        onCheckboxChange={handlePermissionChange}
        label={permission.label}
        labelClassName="m-0"
      />
      {permission.has_transaction_limit && (
        <div className="text-soClean-100 mt-2 flex items-center justify-between text-sm">
          <p>Transaction Max</p>
          <InputIconWrapper
            icon="$"
            className="flex flex-col gap-y-2"
            iconClassName="top-[5px]"
          >
            <NumberInput
              className={`${NUMBER_INPUT_DEFAULT_CLASSES} !w-28 pl-10 !leading-5`}
              value={permission.transactionMaxValue}
              onValueChange={(value) =>
                handleTransactionMaxChange(permission.id, value)
              }
            />
          </InputIconWrapper>
        </div>
      )}
    </div>
  ));

  return (
    <div className="h-[calc(100vh-64px)] overflow-scroll">
      <div
        className="flex flex-col gap-y-2 p-4"
        data-testid="EditUserPermissions"
      >
        <Input
          labelName="Role Name"
          value={roleName}
          onValueChange={setRoleName}
        />
        <TextArea
          className="h-20 resize-none"
          labelName="Role Description"
          value={roleDescription}
          onValueChange={setRoleDescription}
        />
        <div className="border-soFresh-140 -mx-4 mb-1 mt-4 flex items-center justify-between border-t border-solid px-4 pt-4">
          <p className="text-soClean-100 text-xs uppercase leading-6">
            All Permissions
          </p>
          <ResetIconButton
            className="!m-0 !p-0"
            label="Reset permissions"
            onClick={handleResetPermissions}
          />
        </div>
        {permissionsContent}
      </div>
      {!isPermissionsLoading && permissions && (
        <FormActionButtons
          primary={{ onClick: handleSave, disabled: saveDisabled }}
          secondary={{ onClick: onClose }}
        />
      )}
    </div>
  );
};
